import gql from 'graphql-tag';
import { clients } from '../../../util/clients';
import { fieldsToGqlString, mergeByIdNewAtTop } from '../../../util/shared/vue-global';

const { backend } = clients.direct;

const fields = {
  _id: '',
  taxYear: '',
  mills: '',
  levyPercent: '',
  is4Mill: '',
  toUnitDescForAppro: '',
  taxUnit: {
    _id: '',
    toUnitDescForAppro: '',
  },
  taxFund: {
    _id: '',
    fundNumber: '',
    description: '',
  },
  fund: {
    _id: '',
    fund: '',
  },
  taxDistrict: {
    _id: '',
    districtCode: '',
  },
};

const getFundNumber = (record) => ((record.taxFund && record.taxFund.fundNumber) || '');

export const taxLevy = {
  namespaced: true,
  state: () => ({
    regYears: {},
  }),
  mutations: {
    setTaxRegYears(state, regYears) {
      state.regYears = regYears;
    },
  },
  actions: {
    async loadTaxLevy({ commit, dispatch }, {
      criteria, baseCrudKey,
    }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql` query taxLevy($criteria: taxLevyCriteriaType) {
            taxLevy(criteria: $criteria) {
                    ${fieldsToGqlString(fields)},
                  }
                }`,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        if (results.data && results.data.taxLevy) {
          const levy = results.data ? results.data.taxLevy || [] : [];
          /* Should be moved to post-process method */
          const sortLevyTaxFund = levy.sort((a, b) => getFundNumber(a)
            .localeCompare(getFundNumber(b), 'en', { numeric: true }));
          commit('base/crud/setItems', [baseCrudKey, sortLevyTaxFund], { root: true });
          return sortLevyTaxFund;
        }
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was problem loading the Tax Levys list.', { root: true });
      }
      return [];
    },
    async upsertTaxLevy({ dispatch, rootGetters, commit }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertTaxLevy($item: taxLevyInputType!)  {
            upsertTaxLevy(upsert: $item) {
              ${fieldsToGqlString(fields)},
            }
          }`,
          variables: {
            item,
          },
        });
        clients.handleGQLErrors(results, commit);
        const taxFundStateData = rootGetters['base/crud/items'](baseCrudKey);
        const newTaxLevy = results.data.upsertTaxLevy;
        const newList = mergeByIdNewAtTop(taxFundStateData || [], newTaxLevy);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the Tax Fund', { root: true });
      }
    },
    async newYearForTaxLevy({ dispatch, commit }, { item }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation newYearForTaxLevy($item: taxLevyNewYearInputType)  {
            newYearForTaxLevy(upsert: $item) {
              statusCode,
            }
          }`,
          variables: {
            item,
          },
        });
        clients.handleGQLErrors(results, commit);
        const newTaxLevy = results.data.newYearForTaxLevy;
        if (newTaxLevy.statusCode === 200) dispatch('flashSuccess', 'New Tax Year created successfully', { root: true });
        else dispatch('flashError', 'There was a problem in creating the new Tax Year', { root: true });
        return newTaxLevy;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the Tax Fund', { root: true });
      }
      return {};
    },
    async upsertBulkTaxLevy({ dispatch, commit }, { item }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertBulkTaxLevy($item: [taxLevyInputType]!)  {
            upsertBulkTaxLevy(upserts: $item) {
              insertedIds,
            }
          }`,
          variables: {
            item,
          },
        });
        clients.handleGQLErrors(results, commit);
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the Tax Levies!', { root: true });
      }
      return results;
    },
    async loadTaxRegYears({ commit, dispatch }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`{
            taxLevyStatsTYRange { max, min },
          }`,
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        if (results.data) {
          commit('setTaxRegYears', results.data.taxLevyStatsTYRange);
          return results.data.taxLevyStatsTYRange;
        }
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem getting min/max Tax Year ranges.', { root: true });
      }
      return {};
    },
    async deleteTaxLevy({ commit, rootGetters, dispatch }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation deleteTaxLevy ($id: ID!)  {
          deleteTaxLevy(id: $id) {
            _id,
          }
        }`,
          variables: {
            id: item._id,
          },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results, commit);
        const delId = results.data.deleteTaxLevy._id;
        const sharedTransData = rootGetters['base/crud/items'](baseCrudKey);
        const updatedData = sharedTransData.filter((d) => d._id !== delId);
        commit('base/crud/setItems', [baseCrudKey, updatedData], { root: true });
        return delId;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem deleting the item.', { root: true });
      }
      return null;
    },
    async upsertTaxLevyDataForAllYears({ dispatch, rootGetters, commit }, { item, baseCrudKey }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.mutate({
          mutation: gql`mutation upsertTaxLevyDataForAllYears($item: taxLevyInputType!)  {
            upsertTaxLevyDataForAllYears(upsert: $item) {
              result {
                ok
              },
            }
          }`,
          variables: {
            item,
          },
        });
        clients.handleGQLErrors(results, commit);
        const taxFundStateData = rootGetters['base/crud/items'](baseCrudKey);
        const newTaxLevy = results.data.upsertTaxLevyDataForAllYears.result;
        const newList = mergeByIdNewAtTop(taxFundStateData || [], newTaxLevy);
        commit('base/crud/setItems', [baseCrudKey, newList], { root: true });
        return newTaxLevy;
      } catch (e) {
        dispatch('logException', e, { root: true }); clients.handleGQLErrors(results, commit);
        dispatch('flashError', 'There was a problem in upserting the Tax Fund', { root: true });
        return null;
      }
    },
    async validateTaxLevy({ dispatch }, { criteria }) {
      let results;
      try {
        await dispatch('reAuth', null, { root: true });
        results = await backend.query({
          query: gql`
            query validateTaxLevy($criteria: taxLevyCriteriaType) {
              validateTaxLevy(criteria: $criteria) {
                isValid
                messages {
                  message,
                  values
                }
              }
            }
          `,
          variables: { criteria },
          fetchPolicy: 'no-cache',
        });
        clients.handleGQLErrors(results);
        if (results.data && results.data.validateTaxLevy) {
          return results.data.validateTaxLevy;
        }
        return { isValid: true, messages: [] };
      } catch (e) {
        dispatch('logException', e, { root: true });
        clients.handleGQLErrors(results);
        dispatch('flashError', 'There was a problem validating Tax Levy records.', { root: true });
        return { isValid: false, messages: [e.message || 'Unknown error'] };
      }
    },
  },
};

export default taxLevy;
